import React from 'react';
import { Link } from 'react-router-dom';
import CommunityMarquee from '../components/CommunityMarquee';

const AboutUs = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-cream pt-28 sm:pt-32 md:pt-36 pb-12 md:pb-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-dark-green/60 font-mono tracking-[0.25em] text-xs sm:text-sm uppercase mb-5">
            <span className="text-dark-green/40">&rarr;</span> Dharma Initiative &middot; About
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-tight tracking-tight mb-8 max-w-6xl">
            <span className="text-gradient-hero">More than a</span>
            <br />
            <span className="text-gradient-hero-light italic">network.</span>
          </h1>
          <p className="text-dark-green/75 text-lg sm:text-xl md:text-2xl leading-snug max-w-3xl">
            We're rethinking professional connection and success in a dharmic way.
          </p>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="bg-cream pt-4 pb-12 md:pb-16 px-4 sm:px-6 border-t border-dark-green/10">
        <div className="max-w-5xl mx-auto">
          <p className="text-dark-green/60 tracking-[0.25em] text-xs sm:text-sm uppercase mb-3">
            <span className="text-dark-green/40">&rarr;</span> 01 / Our mission
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-dark-green mb-8 md:mb-10 leading-tight">
            What we believe.
          </h2>
          <div className="space-y-4 md:space-y-6 text-dark-green/80 text-base sm:text-lg leading-relaxed">
            <p>
              Have you ever wondered how you can truly make a positive difference? Do you believe there's more to life than simply getting by? The ancient Vedic idea of <span className="italic">dharma</span> represents the pursuit of universal truth, self-understanding, and purpose. At the Dharma Initiative, we take a two-pronged approach to the search for - and service of - dharma. On one hand, we empower young people to uncover their untapped potential and embody their purpose. On the other, we bring together a diverse group of Mentors who share their lived dharma. Together, we cultivate the leaders of tomorrow through the inspirational guidance of the leaders of today.
            </p>
            <p className="font-medium">
              We are a collective of young people and industry luminaries united by one shared aim: to achieve success through helping others, becoming the best we can be.
            </p>
          </div>
        </div>
      </section>

      {/* Community marquee */}
      <CommunityMarquee />

      {/* Our Dharmic Foundation - compact */}
      <section className="bg-dark-green py-12 sm:py-14 md:py-18 px-4 sm:px-6 border-t border-cream/10">
        <div className="max-w-7xl mx-auto">
          {/* Heading row - same line as italic subtitle */}
          <div className="mb-8 md:mb-10 grid md:grid-cols-12 gap-4 md:gap-8 items-end">
            <div className="md:col-span-7">
              <p className="text-cream/50 font-mono tracking-[0.25em] text-xs sm:text-sm uppercase mb-3">
                <span className="text-cream/30">&rarr;</span> 02 / Our compass
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-cream leading-tight">
                Our Dharmic Foundation.
              </h2>
            </div>
            <p className="md:col-span-5 text-cream/55 text-sm md:text-base italic md:text-right">
              Four principles, sourced from the Mahabharata.
            </p>
          </div>

          {/* 4-column compact rows */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-cream/15">
            {[
              {
                devanagari: 'दया',
                sanskrit: 'Dayā',
                english: 'Compassion',
                desc: 'Holding empathy and humanity at the heart of action.',
              },
              {
                devanagari: 'शौच',
                sanskrit: 'Śauca',
                english: 'Cleanliness',
                desc: 'Cultivating purity of intention, preventing clouded judgement.',
              },
              {
                devanagari: 'सत्य',
                sanskrit: 'Satya',
                english: 'Truthfulness',
                desc:
                  'Rooting decisions in fundamental truths. We are spiritual beings having a human experience.',
              },
              {
                devanagari: 'तपस्',
                sanskrit: 'Tapas',
                english: 'Austerity',
                desc:
                  'Detachment from results; focus on intention. Prevents ambition becoming compulsive.',
              },
            ].map((p, i) => (
              <article
                key={p.sanskrit}
                className={`group py-6 md:py-7 px-4 md:px-6 border-b border-cream/15 ${
                  i < 3 ? 'lg:border-r' : ''
                } ${i === 0 ? 'sm:border-r' : ''} ${i === 2 ? 'sm:border-r lg:border-r' : ''} hover:bg-cream/[0.03] transition-colors duration-300`}
              >
                <div className="flex items-baseline gap-2 mb-1.5">
                  <span
                    style={{ fontFamily: "'Noto Serif Devanagari', serif" }}
                    className="text-cream/90 text-lg md:text-xl leading-none"
                  >
                    {p.devanagari}
                  </span>
                  <span className="font-serif italic text-cream/55 text-sm md:text-base">
                    {p.sanskrit}
                  </span>
                </div>
                <h3 className="font-serif text-xl md:text-2xl text-cream leading-tight mb-2">
                  {p.english}
                </h3>
                <p className="text-cream/70 text-xs md:text-sm leading-relaxed">
                  {p.desc}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Find Live Share - Journey strip */}
      <section className="bg-cream py-14 sm:py-18 md:py-22 px-4 sm:px-6 border-t border-dark-green/10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 md:mb-16">
            <p className="text-dark-green/60 font-mono tracking-[0.25em] text-xs sm:text-sm uppercase mb-3">
              <span className="text-dark-green/40">&rarr;</span> 03 / The journey
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-dark-green leading-tight max-w-3xl">
              Find. Live. Share.
            </h2>
          </div>

          {/* Step strip */}
          <div className="relative">
            {/* Horizontal connecting hairline (desktop only) */}
            <div
              aria-hidden
              className="hidden md:block absolute top-[18px] left-[8.33%] right-[8.33%] h-px bg-dark-green/20 pointer-events-none"
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-y-12 md:gap-y-0 md:gap-x-10 lg:gap-x-14">
              {[
                {
                  num: '01',
                  word: 'Find',
                  title: 'your Dharma',
                  desc:
                    'We offer tailored workshops, seminars, resources, and frameworks to help you identify your life calling. We acknowledge how challenging this can be and provide the support you need.',
                },
                {
                  num: '02',
                  word: 'Live',
                  title: 'your Dharma',
                  desc:
                    'We provide exclusive internships, placements, and 1-1 calls with top professionals to help you apply your purpose in practice and nurture your passion.',
                },
                {
                  num: '03',
                  word: 'Share',
                  title: 'your Dharma',
                  desc:
                    'We emphasise giving back through our mentors - successful individuals who mentor others by sharing their experiences, viewing success as a means to contribute.',
                },
              ].map((s) => (
                <div key={s.num} className="relative group">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="relative z-10 inline-flex items-center justify-center w-9 h-9 rounded-full bg-cream border border-dark-green/40 font-mono text-[11px] tracking-widest text-dark-green/70 group-hover:bg-dark-green group-hover:text-cream transition-colors duration-300">
                      {s.num}
                    </span>
                    <span aria-hidden className="md:hidden h-px flex-1 bg-dark-green/15" />
                  </div>
                  <h3 className="font-serif text-4xl sm:text-5xl md:text-6xl text-dark-green leading-[0.95] mb-2">
                    {s.word}
                  </h3>
                  <p className="font-serif italic text-dark-green/55 text-xl md:text-2xl mb-5">
                    {s.title}
                  </p>
                  <p className="text-dark-green/75 text-sm md:text-base leading-relaxed max-w-md">
                    {s.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className="bg-dark-green py-12 sm:py-16 md:py-20 px-4 sm:px-6 border-t border-cream/10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 md:mb-14">
            <p className="text-cream/50 font-mono tracking-[0.25em] text-xs sm:text-sm uppercase mb-3">
              <span className="text-cream/30">&rarr;</span> 04 / Our team
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-cream leading-tight max-w-3xl">
              Our Exec Team<span className="text-cream/45"> driving DI forward.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {/* Raam Gor */}
            <div className="group relative">
              <div className="relative overflow-hidden rounded-2xl md:rounded-3xl shadow-xl mb-4 aspect-[3/4]">
                <img
                  src="/images/raam-gor-team.jpg"
                  alt="Raam Gor"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  style={{ objectPosition: 'center 20%' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-green via-dark-green/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
              <div className="text-center">
                <h3 className="font-serif text-xl md:text-2xl text-cream mb-2">Raam Gor</h3>
                <p className="text-cream/80 text-xs sm:text-sm leading-relaxed min-h-[2.5rem] mb-2">
                  Studying Theology & Religion at the University of Oxford
                </p>
                <span className="inline-block text-[11px] sm:text-xs font-mono tracking-tight uppercase text-cream/75 border border-cream/20 rounded-full px-3 py-1.5">
                  Talent Acquisition
                </span>
              </div>
            </div>

            {/* Gopinath Jani */}
            <div className="group relative">
              <div className="relative overflow-hidden rounded-2xl md:rounded-3xl shadow-xl mb-4 aspect-[3/4]">
                <img
                  src="/images/gopinath-jani-team.jpg"
                  alt="Gopinath Jani"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  style={{ objectPosition: 'center 20%' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-green via-dark-green/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
              <div className="text-center">
                <h3 className="font-serif text-xl md:text-2xl text-cream mb-2">Gopinath Jani</h3>
                <p className="text-cream/80 text-xs sm:text-sm leading-relaxed min-h-[2.5rem] mb-2">
                  Studying Medicine at City St George's University
                </p>
                <span className="inline-block text-[11px] sm:text-xs font-mono tracking-tight uppercase text-cream/75 border border-cream/20 rounded-full px-3 py-1.5">
                  Innovation
                </span>
              </div>
            </div>

            {/* Deven Patel */}
            <div className="group relative">
              <div className="relative overflow-hidden rounded-2xl md:rounded-3xl shadow-xl mb-4 aspect-[3/4]">
                <img
                  src="/images/deven-patel-team.jpg"
                  alt="Deven Patel"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  style={{ objectPosition: 'center 20%' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-green via-dark-green/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
              <div className="text-center">
                <h3 className="font-serif text-xl md:text-2xl text-cream mb-2">Deven Patel</h3>
                <p className="text-cream/80 text-xs sm:text-sm leading-relaxed min-h-[2.5rem] mb-2">
                  Studying Philosophy & Economics at the LSE
                </p>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <span className="inline-block text-[11px] sm:text-xs font-mono tracking-tight uppercase text-cream/75 border border-cream/20 rounded-full px-3 py-1.5">
                    Partnerships
                  </span>
                  <span className="inline-block text-[11px] sm:text-xs font-mono tracking-tight uppercase text-cream/75 border border-cream/20 rounded-full px-3 py-1.5">
                    Finances
                  </span>
                </div>
              </div>
            </div>

            {/* Sumedh Brahmadevara */}
            <div className="group relative">
              <div className="relative overflow-hidden rounded-2xl md:rounded-3xl shadow-xl mb-4 aspect-[3/4]">
                <img
                  src="/images/sumedh-brahmadevara-team.jpg"
                  alt="Sumedh Brahmadevara"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  style={{ objectPosition: 'center 20%' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-green via-dark-green/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
              <div className="text-center">
                <h3 className="font-serif text-xl md:text-2xl text-cream mb-2">Sumedh Brahmadevara</h3>
                <p className="text-cream/80 text-xs sm:text-sm leading-relaxed min-h-[2.5rem] mb-2">
                  Graduate in Economics from the University of Cambridge
                </p>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <span className="inline-block text-[11px] sm:text-xs font-mono tracking-tight uppercase text-cream/75 border border-cream/20 rounded-full px-3 py-1.5">
                    Strategy & Outreach
                  </span>
                  <span className="inline-block text-[11px] sm:text-xs font-mono tracking-tight uppercase text-cream/75 border border-cream/20 rounded-full px-3 py-1.5">
                    Legal
                  </span>
                </div>
              </div>
            </div>

            {/* Guneeka Chitkara */}
            <div className="group relative">
              <div className="relative overflow-hidden rounded-2xl md:rounded-3xl shadow-xl mb-4 aspect-[3/4]">
                <img
                  src="/images/guneeka-chitkara-team.jpg"
                  alt="Guneeka Chitkara"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  style={{ objectPosition: 'center 20%' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-green via-dark-green/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
              <div className="text-center">
                <h3 className="font-serif text-xl md:text-2xl text-cream mb-2">Guneeka Chitkara</h3>
                <p className="text-cream/80 text-xs sm:text-sm leading-relaxed min-h-[2.5rem] mb-2">
                  Studying Physics at UCL
                </p>
                <span className="inline-block text-[11px] sm:text-xs font-mono tracking-tight uppercase text-cream/75 border border-cream/20 rounded-full px-3 py-1.5">
                  Strategy & Outreach
                </span>
              </div>
            </div>

            {/* Chinmay Monga */}
            <div className="group relative">
              <div className="relative overflow-hidden rounded-2xl md:rounded-3xl shadow-xl mb-4 aspect-[3/4]">
                <img
                  src="/images/chinmay-monga-team.jpg"
                  alt="Chinmay Monga"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  style={{ objectPosition: 'center 20%' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-green via-dark-green/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
              <div className="text-center">
                <h3 className="font-serif text-xl md:text-2xl text-cream mb-2">Chinmay Monga</h3>
                <p className="text-cream/80 text-xs sm:text-sm leading-relaxed min-h-[2.5rem] mb-2">
                  Global Markets at UBS Investment Bank
                </p>
                <span className="inline-block text-[11px] sm:text-xs font-mono tracking-tight uppercase text-cream/75 border border-cream/20 rounded-full px-3 py-1.5">
                  DI Online
                </span>
              </div>
            </div>

            {/* Ishika Dav */}
            <div className="group relative">
              <div className="relative overflow-hidden rounded-2xl md:rounded-3xl shadow-xl mb-4 aspect-[3/4]">
                <img
                  src="/images/ishika-dav-team.jpg"
                  alt="Ishika Dav"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  style={{ objectPosition: 'center 20%' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-green via-dark-green/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
              <div className="text-center">
                <h3 className="font-serif text-xl md:text-2xl text-cream mb-2">Ishika Dav</h3>
                <p className="text-cream/80 text-xs sm:text-sm leading-relaxed min-h-[2.5rem] mb-2">
                  Graduate in Computer Science & AI, Strategy at Revolut
                </p>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <span className="inline-block text-[11px] sm:text-xs font-mono tracking-tight uppercase text-cream/75 border border-cream/20 rounded-full px-3 py-1.5">
                    Events
                  </span>
                  <span className="inline-block text-[11px] sm:text-xs font-mono tracking-tight uppercase text-cream/75 border border-cream/20 rounded-full px-3 py-1.5">
                    Community
                  </span>
                </div>
              </div>
            </div>

            {/* Sahil Rathi */}
            <div className="group relative">
              <div className="relative overflow-hidden rounded-2xl md:rounded-3xl shadow-xl mb-4 aspect-[3/4]">
                <img
                  src="/images/sahil-rathi-team.jpg"
                  alt="Sahil Rathi"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  style={{ objectPosition: 'center 20%' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-green via-dark-green/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
              <div className="text-center">
                <h3 className="font-serif text-xl md:text-2xl text-cream mb-2">Sahil Rathi</h3>
                <p className="text-cream/80 text-xs sm:text-sm leading-relaxed min-h-[2.5rem] mb-2">
                  Real Estate at Knight Frank
                </p>
                <span className="inline-block text-[11px] sm:text-xs font-mono tracking-tight uppercase text-cream/75 border border-cream/20 rounded-full px-3 py-1.5">
                  Events
                </span>
              </div>
            </div>
          </div>
          
          {/* Team Growth CTA */}
          <div className="mt-12 md:mt-16 border-t border-cream/15 pt-8 md:pt-10 grid md:grid-cols-12 gap-6 md:gap-10 items-baseline">
            <div className="md:col-span-4">
              <p className="font-mono tracking-[0.25em] text-[10px] sm:text-xs uppercase text-cream/55">
                <span className="text-cream/35">&rarr;</span> Open positions
              </p>
            </div>
            <div className="md:col-span-8">
              <p className="font-serif text-cream text-2xl sm:text-3xl md:text-4xl leading-tight mb-5">
                Our teams are growing.<span className="text-cream/55"> Roles open frequently.</span>
              </p>
              <Link
                to="/join-the-team"
                data-testid="about-join-team-cta"
                className="inline-flex items-center gap-2 font-mono tracking-[0.2em] uppercase text-xs text-cream border-b border-cream/40 hover:border-cream pb-1 transition-colors duration-200"
              >
                Join the team
                <span aria-hidden className="group-hover:translate-x-0.5 transition-transform">&rarr;</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
